import { 
  ResourceRepository,
  GetResourceData,
  CreateResourceData,
  UpdateResourceData,
  GetResourceCategoryData,
  CreateResourceCategoryData,
  UpdateResourceCategoryData
} from '../repository/resource.repository';
import { ResourcesUseCase } from '../usecases/resources.usecase';

export class ResourcesService {
  constructor(
    private resourceRepository: ResourceRepository,
    private resourcesUseCase: ResourcesUseCase
  ) {
    console.log('[ResourcesService] Constructor - Initialized with ResourceRepository and ResourcesUseCase');
  }

  // ===== RESOURCE METHODS =====

  async getAllResources(limit?: number): Promise<GetResourceData[]> {
    console.log('[ResourcesService.getAllResources] START - Limit:', limit);
    try {
      console.log('[ResourcesService.getAllResources] Calling resourceRepository.getAllResources');
      const result = await this.resourceRepository.getAllResources(limit);
      console.log('[ResourcesService.getAllResources] SUCCESS - Retrieved', result.length, 'resources');
      return result;
    } catch (error) {
      console.error('[ResourcesService.getAllResources] ERROR:', error);
      throw error;
    }
  }

  async getResourceById(resourceId: string): Promise<GetResourceData | null> {
    console.log('[ResourcesService.getResourceById] START - ResourceId:', resourceId);
    try {
      console.log('[ResourcesService.getResourceById] Calling resourceRepository.getResourceById');
      const result = await this.resourceRepository.getResourceById(resourceId);
      console.log('[ResourcesService.getResourceById] SUCCESS - Found resource:', !!result);
      return result;
    } catch (error) {
      console.error('[ResourcesService.getResourceById] ERROR:', error);
      throw error;
    }
  }

  async getResourcesByOwner(ownerId: string, limit?: number): Promise<GetResourceData[]> {
    console.log('[ResourcesService.getResourcesByOwner] START - OwnerId:', ownerId, 'Limit:', limit);
    try {
      console.log('[ResourcesService.getResourcesByOwner] Calling resourceRepository.getResourcesByOwner');
      const result = await this.resourceRepository.getResourcesByOwner(ownerId, limit);
      console.log('[ResourcesService.getResourcesByOwner] SUCCESS - Retrieved', result.length, 'resources for owner');
      return result;
    } catch (error) {
      console.error('[ResourcesService.getResourcesByOwner] ERROR:', error);
      throw error;
    }
  }

  async createResource(resourceData: CreateResourceData): Promise<GetResourceData> {
    console.log('[ResourcesService.createResource] START - ResourceData:', resourceData);
    try {
      // Ensure instructorId is set
      if (!resourceData.instructorId) {
        throw new Error('instructorId is required when creating a resource');
      }
      console.log('[ResourcesService.createResource] Creating resource for instructor:', resourceData.instructorId);
      console.log('[ResourcesService.createResource] Calling resourceRepository.createResource');
      const result = await this.resourceRepository.createResource(resourceData);
      console.log('[ResourcesService.createResource] SUCCESS - Created resource:', result.id, 'for instructor:', result.instructorId);
      return result;
    } catch (error) {
      console.error('[ResourcesService.createResource] ERROR:', error);
      throw error;
    }
  }

  async updateResource(resourceId: string, updateData: UpdateResourceData): Promise<GetResourceData> {
    console.log('[ResourcesService.updateResource] START - ResourceId:', resourceId, 'UpdateData:', updateData);
    try {
      console.log('[ResourcesService.updateResource] Calling resourceRepository.updateResource');
      const result = await this.resourceRepository.updateResource(resourceId, updateData);
      console.log('[ResourcesService.updateResource] SUCCESS - Updated resource');
      return result;
    } catch (error) {
      console.error('[ResourcesService.updateResource] ERROR:', error);
      throw error;
    }
  }

  async deleteResource(resourceId: string): Promise<void> {
    console.log('[ResourcesService.deleteResource] START - ResourceId:', resourceId);
    try {
      console.log('[ResourcesService.deleteResource] Calling resourceRepository.deleteResource');
      await this.resourceRepository.deleteResource(resourceId);
      console.log('[ResourcesService.deleteResource] SUCCESS - Deleted resource');
    } catch (error) {
      console.error('[ResourcesService.deleteResource] ERROR:', error);
      throw error;
    }
  }

  // ===== RESOURCE CATEGORY METHODS =====

  async getAllResourceCategories(limit?: number): Promise<GetResourceCategoryData[]> {
    console.log('[ResourcesService.getAllResourceCategories] START - Limit:', limit);
    try {
      console.log('[ResourcesService.getAllResourceCategories] Calling resourceRepository.getAllResourceCategories');
      const result = await this.resourceRepository.getAllResourceCategories(limit);
      console.log('[ResourcesService.getAllResourceCategories] SUCCESS - Retrieved', result.length, 'resource categories');
      return result;
    } catch (error) {
      console.error('[ResourcesService.getAllResourceCategories] ERROR:', error);
      throw error;
    }
  }

  async getResourceCategoryById(categoryId: string): Promise<GetResourceCategoryData | null> {
    console.log('[ResourcesService.getResourceCategoryById] START - CategoryId:', categoryId);
    try {
      console.log('[ResourcesService.getResourceCategoryById] Calling resourceRepository.getResourceCategoryById');
      const result = await this.resourceRepository.getResourceCategoryById(categoryId);
      console.log('[ResourcesService.getResourceCategoryById] SUCCESS - Found category:', !!result);
      return result;
    } catch (error) {
      console.error('[ResourcesService.getResourceCategoryById] ERROR:', error);
      throw error;
    }
  }

  async createResourceCategory(categoryData: CreateResourceCategoryData): Promise<GetResourceCategoryData> {
    console.log('[ResourcesService.createResourceCategory] START - CategoryData:', categoryData);
    try {
      console.log('[ResourcesService.createResourceCategory] Calling resourceRepository.createResourceCategory');
      const result = await this.resourceRepository.createResourceCategory(categoryData);
      console.log('[ResourcesService.createResourceCategory] SUCCESS - Created category:', result.id);
      return result;
    } catch (error) {
      console.error('[ResourcesService.createResourceCategory] ERROR:', error);
      throw error;
    }
  }

  async updateResourceCategory(categoryId: string, updateData: UpdateResourceCategoryData): Promise<GetResourceCategoryData> {
    console.log('[ResourcesService.updateResourceCategory] START - CategoryId:', categoryId, 'UpdateData:', updateData);
    try {
      console.log('[ResourcesService.updateResourceCategory] Calling resourceRepository.updateResourceCategory');
      const result = await this.resourceRepository.updateResourceCategory(categoryId, updateData);
      console.log('[ResourcesService.updateResourceCategory] SUCCESS - Updated category');
      return result;
    } catch (error) {
      console.error('[ResourcesService.updateResourceCategory] ERROR:', error);
      throw error;
    }
  }

  async deleteResourceCategory(categoryId: string): Promise<void> {
    console.log('[ResourcesService.deleteResourceCategory] START - CategoryId:', categoryId);
    try {
      console.log('[ResourcesService.deleteResourceCategory] Calling resourceRepository.deleteResourceCategory');
      await this.resourceRepository.deleteResourceCategory(categoryId);
      console.log('[ResourcesService.deleteResourceCategory] SUCCESS - Deleted category');
    } catch (error) {
      console.error('[ResourcesService.deleteResourceCategory] ERROR:', error);
      throw error;
    }
  }

  // ===== ENHANCED RESOURCE METHODS (via UseCase) =====

  async getResourcesByCategory(categoryId: string, limit?: number): Promise<GetResourceData[]> {
    console.log('[ResourcesService.getResourcesByCategory] START - CategoryId:', categoryId, 'Limit:', limit);
    try {
      console.log('[ResourcesService.getResourcesByCategory] Calling resourceRepository.getResourcesByCategory');
      const result = await this.resourceRepository.getResourcesByCategory(categoryId, limit);
      console.log('[ResourcesService.getResourcesByCategory] SUCCESS - Retrieved', result.length, 'resources');
      return result;
    } catch (error) {
      console.error('[ResourcesService.getResourcesByCategory] ERROR:', error);
      throw error;
    }
  }

  async getResourcesByContentType(contentType: 'video' | 'document' | 'image' | 'audio' | 'text' | 'interactive', limit?: number): Promise<GetResourceData[]> {
    console.log('[ResourcesService.getResourcesByContentType] START - ContentType:', contentType, 'Limit:', limit);
    try {
      console.log('[ResourcesService.getResourcesByContentType] Calling resourceRepository.getResourcesByContentType');
      const result = await this.resourceRepository.getResourcesByContentType(contentType, limit);
      console.log('[ResourcesService.getResourcesByContentType] SUCCESS - Retrieved', result.length, 'resources');
      return result;
    } catch (error) {
      console.error('[ResourcesService.getResourcesByContentType] ERROR:', error);
      throw error;
    }
  }

  async getPublishedResources(limit?: number): Promise<GetResourceData[]> {
    console.log('[ResourcesService.getPublishedResources] START - Limit:', limit);
    try {
      console.log('[ResourcesService.getPublishedResources] Calling resourceRepository.getPublishedResources');
      const result = await this.resourceRepository.getPublishedResources(limit);
      console.log('[ResourcesService.getPublishedResources] SUCCESS - Retrieved', result.length, 'published resources');
      return result;
    } catch (error) {
      console.error('[ResourcesService.getPublishedResources] ERROR:', error);
      throw error;
    }
  }

  // ===== RESOURCE PURCHASE TRACKING METHODS =====

  /**
   * Get users who have purchased a specific resource
   */
  async getResourcePurchasers(resourceId: string): Promise<{
    purchasers: any[];
    totalPurchasers: number;
    totalRevenue: number;
  }> {
    console.log('[ResourcesService.getResourcePurchasers] START - ResourceId:', resourceId);
    
    try {
      // This would query purchase records where items contain this resource
      // For now, return empty structure
      console.log('[ResourcesService.getResourcePurchasers] No purchasers found');
      return {
        purchasers: [],
        totalPurchasers: 0,
        totalRevenue: 0
      };
    } catch (error) {
      console.error('[ResourcesService.getResourcePurchasers] ERROR:', error);
      return {
        purchasers: [],
        totalPurchasers: 0,
        totalRevenue: 0
      };
    }
  }

  /**
   * Get user's purchased resources
   */
  async getUserPurchasedResources(userId: string): Promise<{
    resources: any[];
    totalPurchased: number;
    totalSpent: number;
  }> {
    console.log('[ResourcesService.getUserPurchasedResources] START - UserId:', userId);
    
    try {
      // This would query user's fulfilled orders and extract resources
      // For now, return empty structure
      console.log('[ResourcesService.getUserPurchasedResources] No purchased resources found');
      return {
        resources: [],
        totalPurchased: 0,
        totalSpent: 0
      };
    } catch (error) {
      console.error('[ResourcesService.getUserPurchasedResources] ERROR:', error);
      return {
        resources: [],
        totalPurchased: 0,
        totalSpent: 0
      };
    }
  }

  /**
   * Check if user owns/purchased a specific resource
   */
  async userOwnsResource(userId: string, resourceId: string): Promise<boolean> {
    console.log('[ResourcesService.userOwnsResource] START - UserId:', userId, 'ResourceId:', resourceId);
    
    try {
      const userResources = await this.getUserPurchasedResources(userId);
      const ownsResource = userResources.resources.some((resource: any) => resource.id === resourceId);
      
      console.log('[ResourcesService.userOwnsResource] SUCCESS - Owns resource:', ownsResource);
      return ownsResource;
    } catch (error) {
      console.error('[ResourcesService.userOwnsResource] ERROR:', error);
      return false;
    }
  }

  /**
   * Get resource analytics (views, purchases, ratings)
   */
  async getResourceAnalytics(resourceId: string): Promise<{
    resource: any;
    totalViews: number;
    totalPurchases: number;
    totalRevenue: number;
    averageRating: number;
    recentActivity: any[];
  }> {
    console.log('[ResourcesService.getResourceAnalytics] START - ResourceId:', resourceId);
    
    try {
      // Get resource details
      const resource = await this.getResourceById(resourceId);
      if (!resource) {
        throw new Error('Resource not found');
      }

      // Get purchase data
      const purchaseData = await this.getResourcePurchasers(resourceId);

      // For now, return basic analytics structure
      const analytics = {
        resource,
        totalViews: resource.viewCount || 0,
        totalPurchases: purchaseData.totalPurchasers,
        totalRevenue: purchaseData.totalRevenue,
        averageRating: resource.averageRating || 0,
        recentActivity: []
      };

      console.log('[ResourcesService.getResourceAnalytics] SUCCESS - Analytics compiled');
      return analytics;
    } catch (error) {
      console.error('[ResourcesService.getResourceAnalytics] ERROR:', error);
      throw error;
    }
  }
}
